import { useEffect, useMemo, useState } from "react";
import { Loader2, ShieldCheck, Check, X, UserCheck, UserX, RotateCcw, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

// Default access for a member who hasn't been customised: every non-sensitive tab.
const defaultGrants = (tabsMeta) => tabsMeta.filter((t) => !t.sensitive).map((t) => t.id);

export default function AdminAccess({ tabsMeta }) {
  const [users, setUsers] = useState(null);
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(null); // user id currently saving
  const [draft, setDraft] = useState({}); // userId -> Set(tabIds)

  const load = async () => {
    try {
      const [u, p] = await Promise.all([api.get("/admin-auth/users"), api.get("/admin-auth/pending")]);
      setUsers(u.data.users || []);
      setPending(p.data.pending || []);
      const d = {};
      for (const row of (u.data.users || [])) {
        if (row.role === "founder") continue;
        d[row.id] = new Set(Array.isArray(row.allowed_tabs) ? row.allowed_tabs : defaultGrants(tabsMeta));
      }
      setDraft(d);
    } catch { toast.error("Could not load team members."); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const review = async (id, status) => {
    setBusy(id);
    try { await api.patch(`/admin-auth/users/${id}/review`, { status }); await load(); toast.success(status === "approved" ? "Access approved." : "Request denied."); }
    catch { toast.error("Could not update."); } finally { setBusy(null); }
  };

  const toggle = (uid, tabId) => setDraft((d) => {
    const s = new Set(d[uid]); s.has(tabId) ? s.delete(tabId) : s.add(tabId);
    return { ...d, [uid]: s };
  });
  const setAll = (uid, on) => setDraft((d) => ({ ...d, [uid]: new Set(on ? tabsMeta.map((t) => t.id) : []) }));
  const resetDefault = (uid) => setDraft((d) => ({ ...d, [uid]: new Set(defaultGrants(tabsMeta)) }));

  const saveTabs = async (uid) => {
    setBusy(uid);
    try {
      const allowed_tabs = tabsMeta.map((t) => t.id).filter((id) => draft[uid]?.has(id));
      await api.patch(`/admin-auth/users/${uid}/tabs`, { allowed_tabs });
      await load(); toast.success("Access updated.");
    } catch { toast.error("Could not save access."); } finally { setBusy(null); }
  };

  const members = useMemo(() => (users || []).filter((u) => u.role !== "founder" && u.status === "approved"), [users]);
  const founders = useMemo(() => (users || []).filter((u) => u.role === "founder"), [users]);

  if (!users) return <div className="flex items-center gap-2 text-[#1e3d3a]/60 py-10"><Loader2 className="h-4 w-4 animate-spin" /> Loading team…</div>;

  return (
    <div data-testid="tab-content-access" className="space-y-6">
      <div className="flex items-center gap-2 text-[#1e3d3a]">
        <ShieldCheck className="h-5 w-5 text-[#c9a961]" />
        <h2 className="font-head text-xl">Team access</h2>
      </div>
      <p className="text-sm text-[#1e3d3a]/55 max-w-2xl -mt-3">
        People request access from the sign-in screen. Approve them below, then choose exactly which tabs each
        member sees in their own admin. Sensitive tabs (money &amp; admin) are <span className="text-[#a3823f]">off by default</span> — you can still grant them.
      </p>

      {/* Pending approvals */}
      {pending.length > 0 && (
        <div className="rounded-2xl border border-[#c9a961]/35 bg-[#c9a961]/8 p-5" data-testid="access-pending">
          <h3 className="font-head text-base text-[#1e3d3a] mb-3">Pending requests ({pending.length})</h3>
          <div className="space-y-2">
            {pending.map((u) => (
              <div key={u.id} data-testid={`access-pending-${u.id}`} className="flex items-center justify-between gap-3 rounded-xl bg-white px-4 py-2.5">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#1e3d3a] truncate">{u.display_name || u.username}</div>
                  <div className="text-xs text-[#1e3d3a]/50 truncate">{u.email || u.username}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button data-testid={`access-approve-${u.id}`} disabled={busy === u.id} onClick={() => review(u.id, "approved")} className="inline-flex items-center gap-1 rounded-full bg-[#1e3d3a] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2a534d] disabled:opacity-50"><UserCheck className="h-3.5 w-3.5" /> Approve</button>
                  <button data-testid={`access-deny-${u.id}`} disabled={busy === u.id} onClick={() => review(u.id, "denied")} className="inline-flex items-center gap-1 rounded-full border border-[#1e3d3a]/20 px-3 py-1.5 text-xs text-[#1e3d3a]/70 hover:border-[#b23a48] hover:text-[#b23a48] disabled:opacity-50"><UserX className="h-3.5 w-3.5" /> Deny</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Members with per-tab access */}
      <div className="space-y-4" data-testid="access-members">
        {members.length === 0 && <p className="text-sm text-[#1e3d3a]/50 rounded-2xl border border-[#1e3d3a]/12 bg-white px-5 py-8 text-center">No team members yet. When someone requests access, approve them above and they'll appear here.</p>}
        {members.map((u) => {
          const grants = draft[u.id] || new Set();
          const dirty = JSON.stringify([...grants].sort()) !== JSON.stringify([...(Array.isArray(u.allowed_tabs) ? u.allowed_tabs : defaultGrants(tabsMeta))].sort());
          return (
            <div key={u.id} data-testid={`access-member-${u.id}`} className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div>
                  <div className="font-head text-base text-[#1e3d3a]">{u.display_name || u.username}</div>
                  <div className="text-xs text-[#1e3d3a]/50">{u.email || u.username} · {grants.size} of {tabsMeta.length} tabs</div>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`access-all-${u.id}`} onClick={() => setAll(u.id, true)} className="text-[11px] text-[#1e3d3a]/55 hover:text-[#c9a961]">All</button>
                  <button data-testid={`access-none-${u.id}`} onClick={() => setAll(u.id, false)} className="text-[11px] text-[#1e3d3a]/55 hover:text-[#c9a961]">None</button>
                  <button data-testid={`access-reset-${u.id}`} onClick={() => resetDefault(u.id)} className="inline-flex items-center gap-1 text-[11px] text-[#1e3d3a]/55 hover:text-[#c9a961]"><RotateCcw className="h-3 w-3" /> Default</button>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {tabsMeta.map((t) => {
                  const on = grants.has(t.id);
                  return (
                    <button key={t.id} data-testid={`access-tab-${u.id}-${t.id}`} onClick={() => toggle(u.id, t.id)}
                      className={`flex items-center justify-between gap-1.5 rounded-lg border px-2.5 py-2 text-left text-xs transition-colors ${on ? "border-[#c9a961] bg-[#c9a961]/12 text-[#1e3d3a]" : "border-[#1e3d3a]/12 bg-[#f4f1e9] text-[#1e3d3a]/45"}`}>
                      <span className="flex items-center gap-1 truncate">{t.sensitive && <Lock className="h-3 w-3 text-[#a3823f] shrink-0" />}{t.label}</span>
                      {on ? <Check className="h-3.5 w-3.5 text-[#c9a961] shrink-0" /> : <X className="h-3.5 w-3.5 text-[#1e3d3a]/25 shrink-0" />}
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 flex justify-end">
                <button data-testid={`access-save-${u.id}`} disabled={!dirty || busy === u.id} onClick={() => saveTabs(u.id)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#c9a961] px-4 py-2 text-sm font-semibold text-[#1a2e2b] hover:bg-[#d8b972] disabled:opacity-40">
                  {busy === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {dirty ? "Save access" : "Saved"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Founders (reference) */}
      {founders.length > 0 && (
        <div className="rounded-2xl border border-[#1e3d3a]/10 bg-[#f4f1e9] px-5 py-4" data-testid="access-founders">
          <div className="text-[10px] uppercase tracking-wide text-[#1e3d3a]/45 mb-1">Founders · full access</div>
          <div className="text-sm text-[#1e3d3a]/70">{founders.map((f) => f.display_name || f.username).join(" · ")}</div>
        </div>
      )}
    </div>
  );
}
